import poiUtils, { mergeCategoriesByClientCode } from '@/utils/poi';
import type { BuildingCategory, BuildingInStoreInfo, FloorInfo, KioskSelectedCategory, LocalizedString } from '@/services/dto';

type LocalizedNameEntry = {
    code?: string;
    lang?: string;
    name?: string;
    text?: string;
};

const toLocalizedString = (value: LocalizedString | LocalizedNameEntry[] | unknown): LocalizedString => {
    if (!Array.isArray(value)) {
        return (value as LocalizedString) ?? { ko: '', en: '' };
    }

    const result: LocalizedString = { ko: '', en: '' };
    for (const item of value) {
        const code = item.code ?? item.lang;
        const text = item.name ?? item.text ?? '';
        if (code) {
            result[code] = text;
        }
    }
    return result;
};

const normalizeFloors = (floors: FloorInfo[] | undefined): FloorInfo[] =>
    floors?.map((floor) => ({
        ...floor,
        name: toLocalizedString(floor.name),
        detail: toLocalizedString(floor.detail),
    })) ?? [];

const sortFacilityCategoriesByCode = (categories: BuildingCategory[]): BuildingCategory[] => {
    const facilityCategoryOrder = [
        'all', // 전체 (All)
        '140100', // 서비스 (Service)
        '140200', // 문화/웨딩 (Culture/Wedding)
        '140300', // 금융 (Finance)
        '140400', // A/S (After-sales Service)
        '140500', // 기타 (Other) - includes Fun, 자동차
        '140800', // 기타 (Other) - excludes 매장검색 > 기타, 주차장
    ];

    return categories.sort((a, b) => {
        const aIndex = facilityCategoryOrder.indexOf(a.clientCode);
        const bIndex = facilityCategoryOrder.indexOf(b.clientCode);

        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        return a.clientCode.localeCompare(b.clientCode);
    });
};
export const sortCategories = (categories: KioskSelectedCategory[]) => {
    return [...categories].sort((a, b) => {
        if (a.clientCode === 'all') return -1;
        if (b.clientCode === 'all') return 1;

        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        return nameA.localeCompare(nameB, 'ko', { numeric: true, sensitivity: 'base' });
    });
};

const sortFnbCategoriesByCode = (categories: BuildingCategory[]): BuildingCategory[] => {
    const fnbCategoryOrder = [
        'all', // 전체 (All)
        '120201', // 한식 (Korean Food)
        '120202', // 일식 (Japanese Food)
        '120203', // 중식 (Chinese Food)
        '120204', // 양식 (Western Food)
        '120205', // 아시아푸드 (Asian Food)
        '120401', // 카페 (Cafe)
        '120103', // 가드닝카페 (Gardening Cafe)
        '120207', // 기타 (Other)
        '120101', // 푸드코트/델리 (Food Court/Deli)
        '120301', // 베이커리/디저트 (Bakery/Dessert)
    ];

    return categories.sort((a, b) => {
        const aIndex = fnbCategoryOrder.indexOf(a.clientCode);
        const bIndex = fnbCategoryOrder.indexOf(b.clientCode);

        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        return a.clientCode.localeCompare(b.clientCode);
    });
};

const sortCategoriesByCode = (categories: BuildingCategory[]): BuildingCategory[] => {
    const categoryOrder = [
        'all', // 전체 (All) - clientCode is 'all'
        '010000', // 여성패션 (Women's Fashion)
        '020000', // 영패션 (Young Fashion)
        '040000', // 패션잡화 (Fashion Accessories)
        '050000', // 화장품 (Cosmetics)
        '060000', // 남성패션 (Men's Fashion)
        '070000', // 레져/스포츠 (Leisure/Sports)
        '080000', // 아동/유아 (Children/Infants)
        '090000', // 해외패션 (International Fashion)
        '100000', // 리빙 (Living)
        '110000', // 식품관 (Food Hall)
        '120000', // 다이닝 (Dining)
        '130000', // 행사장 (Event Hall)
        '140000', // 기타 (Other) - added this code that appears in the data
        '150000', // 기타 (Other)
    ];

    return categories.sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.clientCode);
        const bIndex = categoryOrder.indexOf(b.clientCode);

        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        return a.clientCode.localeCompare(b.clientCode);
    });
};

const processBuildings = (buildings: BuildingInStoreInfo[]) => {
    const optionAll: BuildingCategory = {
        clientCode: 'all',
        name: {
            ko: 'common.text.all_category',
            en: 'common.text.all_category',
            ja: 'common.text.all_category',
            'zh-CN': 'common.text.all_category',
            'zh-TW': 'common.text.all_category',
        },
        id: '',
        parentClientCode: '',
        indentLevel: 0,
        categoryCode: '',
        parentCategoryCode: '',
    };

    const poiContents = buildings?.flatMap((item) => item?.poiContents ?? []).filter(Boolean) ?? [];

    return (
        buildings?.map((building) => {
            const { shopPoiContents, fnbPoiContents, facilityPoiContents, otherUtilityPoiContents } = poiUtils.filterPoiContents(building, poiContents);
            const { shopCategories, fnbCategories, facilityCategories, shopRootCategory, fnbRootCategory, facilityRootCategory, otherFacilityCategories } =
                poiUtils.filterCategories(building, poiContents);

            return {
                ...building,
                name: toLocalizedString(building.name),
                floors: normalizeFloors(building.floors),
                filterPoiContents: {
                    shop: shopPoiContents,
                    fnb: fnbPoiContents,
                    facility: facilityPoiContents,
                    otherUtility: otherUtilityPoiContents,
                },
                filterCategories: {
                    shop: sortCategoriesByCode([optionAll, ...shopCategories]),
                    fnb: sortFnbCategoriesByCode([optionAll, ...fnbCategories]),
                    facility: sortFacilityCategoriesByCode([optionAll, ...facilityCategories]),
                    otherFacility: [optionAll, ...otherFacilityCategories],
                },
                rootCategories: {
                    shop: shopRootCategory,
                    fnb: fnbRootCategory,
                    facility: facilityRootCategory,
                },
            };
        }) || []
    );
};

type CategoryGroupMap = Record<string, string[]>;

export type MergedCategoryResult = {
    categories: BuildingCategory[];
    codesByClientCode: CategoryGroupMap;
};

const mergeCategories = (buildings: BuildingInStoreInfo[], buildingId?: string) => {
    const empty = {
        shop: [] as BuildingCategory[],
        fnb: [] as BuildingCategory[],
        facility: [] as BuildingCategory[],
    };

    if (buildings.length <= 1) {
        return empty;
    }

    const allShopCategories = buildings.flatMap((b) => (b.filterCategories?.shop ?? []).filter((c) => c.clientCode !== 'all'));
    const shopResult = mergeCategoriesByClientCode(allShopCategories);
    const optionAll = buildings.find((b) => b.filterCategories?.shop)?.filterCategories?.shop?.find((c) => c.clientCode === 'all');

    return {
        shop: optionAll ? [optionAll, ...shopResult] : shopResult,
        fnb: [],
        facility: [],
    };
};

const configStoreUtils = {
    processBuildings,
    mergeCategories,
};

export default configStoreUtils;
